import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from './components/AdminLayout';
import { API_BASE_URL } from '../../config/apiConfig';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const PageTitle = styled.h1`
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
`;

const SplitContainer = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 24px;
  align-items: start;
  
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
`;

const CardBody = styled.div`
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 11px 14px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: white;
  font-size: 14px;
  color: #495057;
  outline: none;
  transition: border-color 0.15s ease-in-out;
  cursor: pointer;
  
  &:focus {
    border-color: #2c3e50;
  }
`;

const MetaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px dashed #dee2e6;
`;

const MetaItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
`;

const MetaLabel = styled.span`
  color: #6c757d;
  font-weight: 500;
`;

const MetaValue = styled.span`
  color: #2c3e50;
  font-weight: 600;
  text-align: right;
`;

const PreviewPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 600px;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  gap: 12px;
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 14px;
  padding: 10px;
  background: #f8d7da;
  border-radius: 6px;
  border: 1px solid #f5c6cb;
  margin-bottom: 20px;
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ActionButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 16px;
  background: #e9fe53;
  color: #202023;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 20px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    background: #d4e94a;
  }
`;

export function InvoiceView() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [error, setError] = useState(null);
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState('');
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Fetch all invoices on mount
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const token = sessionStorage.getItem('admin-token');
        if (!token) {
          navigate('/admin/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/admin/invoices?limit=1000`, {
          headers: { 'Authorization': token }
        });
        const result = await response.json();

        if (result.is_success && result.data && result.data.invoices) {
          setInvoices(result.data.invoices);
        } else {
          setError(result.error || 'Failed to fetch invoices list');
        }
      } catch (err) {
        setError('Network error: Failed to connect to server');
      } finally {
        setLoadingList(false);
      }
    };

    fetchInvoices();
  }, [navigate]);

  // Load PDF when invoice number selection changes
  useEffect(() => {
    if (!selectedInvoiceNumber) {
      setPdfUrl(null);
      setSelectedInvoiceData(null);
      return;
    }

    const fetchPdf = async () => {
      setLoadingPdf(true);
      setError(null);
      try {
        const token = sessionStorage.getItem('admin-token');
        const response = await fetch(`${API_BASE_URL}/admin/invoices/${encodeURIComponent(selectedInvoiceNumber)}/pdf`, {
          headers: { 'Authorization': token }
        });

        if (response.status === 200) {
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          setPdfUrl(objectUrl);

          // Find full invoice data from the list
          const match = invoices.find(inv => inv.invoiceNumber === selectedInvoiceNumber);
          setSelectedInvoiceData(match || null);
        } else {
          setError(`Failed to fetch PDF invoice (${response.status})`);
        }
      } catch (err) {
        setError('Failed to download invoice PDF');
      } finally {
        setLoadingPdf(false);
      }
    };

    fetchPdf();
  }, [selectedInvoiceNumber, invoices]);

  // Cleanup object URLs to prevent leaks
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  return (
    <AdminLayout>
      <PageHeader>
        <PageTitle>View Invoice</PageTitle>
      </PageHeader>

      {error && <ErrorText>{error}</ErrorText>}

      <SplitContainer>
        {/* Left Column: Dropdown Selector */}
        <Card>
          <CardHeader>
            <CardTitle>Select Invoice</CardTitle>
          </CardHeader>
          <CardBody>
            <FormGroup>
              <Label htmlFor="invoice-select">Invoice Number</Label>
              {loadingList ? (
                <LoadingText>Loading invoices list...</LoadingText>
              ) : (
                <Select
                  id="invoice-select"
                  value={selectedInvoiceNumber}
                  onChange={(e) => setSelectedInvoiceNumber(e.target.value)}
                >
                  <option value="">-- Choose Invoice --</option>
                  {invoices.map((inv) => (
                    <option key={inv.invoiceNumber} value={inv.invoiceNumber}>
                      {inv.invoiceNumber} {inv.user ? `(${inv.user.firstname} ${inv.user.lastname})` : ''}
                    </option>
                  ))}
                </Select>
              )}
            </FormGroup>

            {selectedInvoiceData && (
              <MetaList>
                <MetaItem>
                  <MetaLabel>Customer</MetaLabel>
                  <MetaValue>
                    {selectedInvoiceData.user
                      ? `${selectedInvoiceData.user.firstname} ${selectedInvoiceData.user.lastname}`
                      : 'Customer'}
                  </MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Date</MetaLabel>
                  <MetaValue>
                    {selectedInvoiceData.details?.issueDate ||
                      new Date(selectedInvoiceData.issueDate || selectedInvoiceData.createdAt).toLocaleDateString('en-IN')}
                  </MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Order Number</MetaLabel>
                  <MetaValue style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                    {selectedInvoiceData.orderId || '-'}
                  </MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Total Amount</MetaLabel>
                  <MetaValue>₹{(Number(selectedInvoiceData.amount) || 0).toLocaleString('en-IN')}</MetaValue>
                </MetaItem>
                
                {pdfUrl && (
                  <ActionButton href={pdfUrl} download={`${selectedInvoiceNumber}.pdf`}>
                    Download Invoice PDF
                  </ActionButton>
                )}
              </MetaList>
            )}
          </CardBody>
        </Card>

        {/* Right Column: PDF Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Invoice Preview</CardTitle>
          </CardHeader>
          <CardBody style={{ padding: '12px' }}>
            {loadingPdf ? (
              <LoadingText style={{ height: '600px' }}>Generating invoice PDF preview...</LoadingText>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                width="100%"
                height="650px"
                style={{ border: 'none', borderRadius: '4px' }}
                title="Invoice Preview Frame"
              />
            ) : (
              <PreviewPlaceholder>
                <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Please select an invoice from the dropdown on the left.</span>
              </PreviewPlaceholder>
            )}
          </CardBody>
        </Card>
      </SplitContainer>
    </AdminLayout>
  );
}
